import { gql, useLazyQuery } from "@apollo/client";
import { useMemo, useState } from "react";

/**
 * Shared "recently messaged" guard for every chat-sending tool.
 *
 * Reads the one shared outreach log, filtered to actual CHAT messages
 * (`types: [MESSAGE]`) — so a shared-event notification or an event invite
 * never counts as contact. Any tool that DMs members uses this, so we never
 * pile several messages on the same person, and they all see each other's sends.
 */
export const RECENT_MESSAGES = gql`
  query RecentMessages($userIds: [String!]!, $withinDays: Int!) {
    adminRecentOutreach(userIds: $userIds, withinDays: $withinDays, types: [MESSAGE]) {
      userId
      lastAt
      lastType
      lastCampaign
      count
    }
  }
`;

/** Which tool last messaged a member, from the outreach log's campaign tag. */
export const messageToolLabel = (campaign?: string | null): string | null => {
  if (!campaign) {
    return null;
  }
  if (campaign.startsWith("cancellation")) {
    return "Cancellations";
  }
  if (campaign.startsWith("online_now")) {
    return "Online Now";
  }
  if (campaign.startsWith("retention")) {
    return "Retention";
  }
  if (campaign.startsWith("share")) {
    return "Share Event";
  }
  return null;
};

export interface RecentMessageInfo {
  /** Whole days since she was last sent a chat message. */
  days: number;
  /** The tool that sent it (campaign tag), if known. */
  campaign?: string | null;
  /** How many messages in the look-back window. */
  count: number;
}

/**
 * Look up recent chat messages to a set of members and decide who to skip.
 * `loadFor` fetches a wide window (so the skip control works without a
 * re-fetch); `contactedWithinSkip` answers per member against `skipDays`.
 */
export const useRecentMessages = (defaultSkipDays = 14) => {
  const [skipDays, setSkipDays] = useState(defaultSkipDays);
  const [load, { data }] = useLazyQuery(RECENT_MESSAGES, { fetchPolicy: "network-only" });

  const recentByUser = useMemo(() => {
    const map = new Map<string, RecentMessageInfo>();
    for (const row of data?.adminRecentOutreach ?? []) {
      const days = Math.floor((Date.now() - new Date(row.lastAt).getTime()) / 86_400_000);
      map.set(row.userId, { days, campaign: row.lastCampaign, count: row.count });
    }
    return map;
  }, [data]);

  const loadFor = (userIds: string[]): void => {
    if (userIds.length > 0) {
      void load({ variables: { userIds, withinDays: 60 } });
    }
  };

  const contactedWithinSkip = (userId: string): boolean => {
    const r = recentByUser.get(userId);
    return r !== undefined && r.days <= skipDays;
  };

  return { skipDays, setSkipDays, loadFor, recentByUser, contactedWithinSkip };
};
